/**
 * BranchStorePage — Loja pública de uma filial.
 * Rota: /filial/:branchSlug/loja
 */
import { useParams, useLocation } from "wouter";
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Loader2, Building2, AlertCircle, ArrowLeft, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function BranchStorePage() {
  const params = useParams<{ branchSlug: string }>();
  const branchSlug = params.branchSlug;
  const [, navigate] = useLocation();

  const { data, isLoading, error } = (trpc as any).branches.getPublic.useQuery(
    { slug: branchSlug ?? "" },
    { enabled: !!branchSlug }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm">Carregando loja da filial...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-center px-4">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold">Filial não encontrada</h1>
          <p className="text-sm text-muted-foreground max-w-xs">
            Esta filial não existe ou está temporariamente desativada.
          </p>
          <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  const { branch, parent } = data;
  const parentSlug = parent?.slug ?? "";

  return (
    <div className="min-h-screen bg-background">
      {/* Banner da filial */}
      <div className="bg-card border-b border-border px-4 py-3 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(`/filial/${branch.slug}`)}
            className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm transition-colors mr-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {branch.logoUrl ? (
            <img src={branch.logoUrl} alt={branch.name} className="w-7 h-7 rounded-md object-cover border border-border" />
          ) : (
            <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-primary" />
            </div>
          )}
          <div>
            <p className="font-semibold text-sm leading-tight flex items-center gap-1.5">
              <ShoppingBag className="w-3.5 h-3.5" />
              {branch.name} — Loja
            </p>
            <p className="text-xs text-muted-foreground">Filial de {parent?.name}</p>
          </div>
        </div>
      </div>

      {/* Conteúdo da loja */}
      <BranchStoreContent
        parentSlug={parentSlug}
        branchName={branch.name}
        branchSlug={branch.slug}
        navigate={navigate}
      />
    </div>
  );
}

// ── Componente de loja da filial ─────────────────────────────────────────────
function BranchStoreContent({
  parentSlug,
  branchName,
  branchSlug,
  navigate,
}: {
  parentSlug: string;
  branchName: string;
  branchSlug: string;
  navigate: (path: string) => void;
}) {
  const [cart, setCart] = useState<Array<{ id: number; name: string; salePrice: string; qty: number; imageUrl?: string | null; brand?: string | null }>>([]);
  const [orderForm, setOrderForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [orderSent, setOrderSent] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const { data: products, isLoading } = trpc.products.listPublic.useQuery(
    { salonSlug: parentSlug },
    { enabled: !!parentSlug }
  );

  const requestPurchaseMutation = trpc.products.requestPurchase.useMutation({
    onSuccess: () => {
      setOrderSent(true);
      setCart([]);
    },
    onError: (e: any) => alert(e.message ?? "Erro ao enviar pedido. Tente novamente."),
  });

  const activeProducts = products ?? [];
  const categories = ["all", ...Array.from(new Set(activeProducts.map((p) => p.category).filter(Boolean))) as string[]];
  const filtered = activeProducts.filter((p) => selectedCategory === "all" || p.category === selectedCategory);

  const cartTotal = cart.reduce((sum, item) => sum + item.qty * Number(item.salePrice), 0);

  function addToCart(product: typeof activeProducts[0]) {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { id: product.id, name: product.name, salePrice: String(product.salePrice), qty: 1, imageUrl: product.imageUrl, brand: product.brand }];
    });
  }

  function removeFromCart(id: number) {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }

  function submitOrder() {
    if (!orderForm.name || !orderForm.phone) return;
    requestPurchaseMutation.mutate({
      salonSlug: parentSlug,
      clientName: orderForm.name,
      clientPhone: orderForm.phone || undefined,
      notes: orderForm.notes || undefined,
      items: cart.map((i) => ({
        productId: i.id,
        productName: i.name,
        quantity: i.qty,
        unitPrice: Number(i.salePrice),
      })),
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (orderSent) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
          <span className="text-3xl">🎉</span>
        </div>
        <h2 className="text-xl font-bold">Pedido enviado!</h2>
        <p className="text-muted-foreground text-sm">
          Recebemos seu pedido na {branchName}. Entraremos em contato em breve para confirmar.
        </p>
        <Button onClick={() => { setOrderSent(false); navigate(`/filial/${branchSlug}`); }}>
          Voltar à filial
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Produtos */}
        <div className="flex-1">
          <h2 className="text-xl font-bold mb-4">Produtos disponíveis</h2>

          {categories.length > 1 && (
            <div className="flex gap-2 flex-wrap mb-5">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {cat === "all" ? "Todos" : cat}
                </button>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Nenhum produto disponível no momento.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filtered.map((product) => (
                <div key={product.id} className="border border-border rounded-xl p-4 flex gap-3">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} className="w-16 h-16 rounded-lg object-cover border border-border shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <ShoppingBag className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{product.name}</p>
                    {product.brand && <p className="text-xs text-muted-foreground">{product.brand}</p>}
                    <p className="font-bold text-primary mt-1">
                      R$ {Number(product.salePrice).toFixed(2).replace(".", ",")}
                    </p>
                    <Button size="sm" className="mt-2 h-7 text-xs" onClick={() => addToCart(product)}>
                      + Adicionar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Carrinho */}
        {cart.length > 0 && (
          <div className="lg:w-80 shrink-0">
            <div className="border border-border rounded-xl p-4 sticky top-20 space-y-4">
              <h3 className="font-bold">Seu pedido</h3>
              <div className="space-y-2">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex-1 truncate">{item.name} ×{item.qty}</span>
                    <span className="font-medium">R$ {(item.qty * Number(item.salePrice)).toFixed(2).replace(".", ",")}</span>
                    <button onClick={() => removeFromCart(item.id)} className="text-destructive text-xs">✕</button>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-bold">
                <span>Total</span>
                <span>R$ {cartTotal.toFixed(2).replace(".", ",")}</span>
              </div>

              <div className="space-y-2">
                <input
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  placeholder="Seu nome *"
                  value={orderForm.name}
                  onChange={(e) => setOrderForm((f) => ({ ...f, name: e.target.value }))}
                />
                <input
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  placeholder="WhatsApp *"
                  value={orderForm.phone}
                  onChange={(e) => setOrderForm((f) => ({ ...f, phone: e.target.value }))}
                />
                <input
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  placeholder="E-mail (opcional)"
                  value={orderForm.email}
                  onChange={(e) => setOrderForm((f) => ({ ...f, email: e.target.value }))}
                />
                <textarea
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background resize-none"
                  placeholder="Observações (opcional)"
                  rows={2}
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>

              <Button
                className="w-full"
                disabled={!orderForm.name || !orderForm.phone || requestPurchaseMutation.isPending}
                onClick={submitOrder}
              >
                {requestPurchaseMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Finalizar pedido"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
